import { Controller } from "@hotwired/stimulus";
import {
  AI_ASSISTANT_WIDTH_DEFAULT,
  AI_ASSISTANT_WIDTH_MAX,
  AI_ASSISTANT_WIDTH_MIN,
  applyAiAssistantWidthPx,
  applyStoredAiAssistantWidth,
  clampAiAssistantWidthPx,
  readStoredAiAssistantWidthPx,
} from "../utils/ai_assistant_width_sync";

const KEYBOARD_STEP = 16;

export default class extends Controller {
  static targets = ["handle"];

  connect() {
    applyStoredAiAssistantWidth();
  }

  start(event) {
    if (this.element.dataset.aiAssistantLayout !== "opened") return;

    event.preventDefault();
    event.stopPropagation();
    if (this.hasHandleTarget) {
      this.handleTarget.setPointerCapture(event.pointerId);
    }
    this.dragging = true;
    this.startX = event.clientX;
    this.startWidth = this.currentWidth();
    this.dragWidth = this.startWidth;
    this.element.classList.add("workflow-step-shell--resizing-ai");
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    document.addEventListener("pointermove", this.boundMove);
    document.addEventListener("pointerup", this.boundStop);
    document.addEventListener("pointercancel", this.boundStop);
  }

  disconnect() {
    this.stopDrag();
  }

  boundMove = (event) => {
    if (!this.dragging) return;

    event.preventDefault();
    const rawWidth = this.startWidth - (event.clientX - this.startX);
    this.dragWidth = this.clamp(rawWidth);
    this.setWidth(this.dragWidth, { persist: false });
  };

  boundStop = () => {
    if (!this.dragging) return;

    this.dragging = false;
    this.element.classList.remove("workflow-step-shell--resizing-ai");
    document.body.style.cursor = "";
    document.body.style.userSelect = "";

    document.removeEventListener("pointermove", this.boundMove);
    document.removeEventListener("pointerup", this.boundStop);
    document.removeEventListener("pointercancel", this.boundStop);

    const width = this.dragWidth ?? this.currentWidth();
    this.setWidth(width, { persist: true });
    this.dragWidth = null;
  };

  stopDrag() {
    if (!this.dragging) return;
    this.boundStop();
  }

  reset() {
    applyAiAssistantWidthPx(AI_ASSISTANT_WIDTH_DEFAULT, { persist: true });
  }

  keydown(event) {
    if (!this.hasHandleTarget || document.activeElement !== this.handleTarget) return;

    let next = this.currentWidth();
    if (event.key === "ArrowLeft") next += KEYBOARD_STEP;
    if (event.key === "ArrowRight") next -= KEYBOARD_STEP;
    if (event.key === "Home") next = AI_ASSISTANT_WIDTH_MAX;
    if (event.key === "End") next = AI_ASSISTANT_WIDTH_MIN;
    if (next === this.currentWidth()) return;

    event.preventDefault();
    this.setWidth(this.clamp(next), { persist: true });
  }

  clamp(px) {
    return clampAiAssistantWidthPx(px);
  }

  currentWidth() {
    const panel = this.element.querySelector(".workflow-step-shell__ai-assistant");
    const measured = panel?.getBoundingClientRect().width;
    if (measured && measured > 0) return measured;

    const stored = readStoredAiAssistantWidthPx();
    if (stored) return stored;

    const raw = getComputedStyle(document.documentElement).getPropertyValue("--ai-assistant-panel-width");
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? AI_ASSISTANT_WIDTH_DEFAULT : parsed;
  }

  setWidth(px, { persist = false }) {
    applyAiAssistantWidthPx(px, { persist });
    if (this.hasHandleTarget) {
      this.handleTarget.setAttribute("aria-valuenow", String(this.clamp(px)));
    }
  }
}
